import { Genders } from 'Constants';
import { _config } from './envConfig';
import i18n from './i18n';
import { getSessionObject } from './storage';

/** Reference used to store urls */
export const urls = {
  apiUrl: ''
};

/**
 * Sets the urls object to be ready for services
 * - config: object - The environment config
 */
export const setUrls = (config, country) => {
  const {
    apiCountryUrls
  } = config;
  urls.apiUrl = apiCountryUrls !== null && apiCountryUrls !== void 0 && apiCountryUrls[country] ? normalizeUrl(apiCountryUrls[country]) : '';
};

/**
 * Sets the urls object based on the country redirect URL if a user tries to login hitting the incorrect country URL
 * @param url: The redirect url returned in the x-api-url header on a 400 code 5 error (unsupported country in api region)
 */
export const setCountryRedirectUrl = url => {
  urls.apiUrl = normalizeUrl(url);
};
export const setInitialApiUrl = (config, app) => {
  const apiUrl = app === 'llu' ? config.lluApiUrl : config.libreApiUrl;
  urls.apiUrl = normalizeUrl(apiUrl);
};
export const getDocumentPathFromLanguage = (document, lang) => {
  if (!document) {
    return '';
  }
  const path = document[lang.toLowerCase()] || document.default || document['en-us'];
  return path;
};
export const detectTheme = urlParams => {
  const productParam = urlParams.get('product');
  const theme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'Dark' : 'Light';
  return !productParam || productParam === 'libre1' ? `polaris${theme}` : `otc${theme}`;
};
export const getTheme = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const themeParam = urlParams.get('theme');
  return themeParam ? themeParam : detectTheme(urlParams);
};
export const getApp = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const app = urlParams.get('app') || urlParams.get('product');
  return app ?? 'libre1';
};
export const getUserResourcePath = app => {
  switch (app) {
    case 'libre1':
      return 'patients';
    case 'llu':
      return 'caregivers';
    default:
      return 'patients';
  }
};
export const getUpdateUserMethod = app => {
  switch (app) {
    case 'libre1':
      return 'PUT';
    case 'llu':
      return 'PATCH';
    default:
      return 'PATCH';
  }
};
export const getUpdatePasswordPath = app => {
  switch (app) {
    case 'libre1':
      return 'accounts';
    case 'llu':
      return 'caregivers';
    default:
      return 'accounts';
  }
};
export const getTermsOfUseId = app => {
  switch (app) {
    case 'libre1':
      return 'touLibre';
    case 'llu':
      return 'toullu';
    default:
      return 'touLibre';
  }
};
export const generateUserAgentHeader = (app, appVersion, osType, osVersion) => {
  return `${app ?? 'none'};${appVersion ?? 'none'};${osType ?? 'none'};${osVersion ?? 'none'}`;
};
export const normalizeUrl = url => {
  const normalizedUrl = url.replace(/\/v1\/?$/, '/');
  return normalizedUrl;
};
export const validateFrenchZipCode = zipCode => {
  if (!zipCode) {
    return false;
  }
  const regex = new RegExp(/^\d{5}$/);
  return regex.test(zipCode);
};
export const getGenderDisplayText = gender => {
  if (!gender) {
    return '';
  }
  const cmsKey = gender === Genders.UNDETERMINED ? 'other' : gender;
  return i18n.t(`Global.microcopy.genderOptions.${cmsKey}`);
};
export const formatMobileNumber = parsedPhoneNumber => {
  if (!parsedPhoneNumber) {
    return '';
  }
  const formattedNumber = parsedPhoneNumber === null || parsedPhoneNumber === void 0 ? void 0 : parsedPhoneNumber.format('NATIONAL');
  const countryCode = parsedPhoneNumber === null || parsedPhoneNumber === void 0 ? void 0 : parsedPhoneNumber.countryCallingCode;
  return `+${countryCode} ${formattedNumber}`;
};
export const isBypassPhoneNumberEnabled = () => {
  return Boolean(_config.environment !== 'production' && getSessionObject('isBypassPhoneNumberEnabled'));
};
export const getNestedValue = (obj, path) => path.split('.').reduce((acc, key) => acc === null || acc === void 0 ? void 0 : acc[key], obj);
export const hasPendingConsent = consent => {
  if (!consent || !consent.accepted) {
    return false;
  }
  const {
    at,
    latestVersion
  } = consent;
  const isOutdated = !at || at < latestVersion;
  return isOutdated;
};

/**
 * Verifies if the user's country supports consents.
 *
 * @param patient - The patient object containing consent records.
 * @returns true or false
 */
export const hasConsentsSupport = patient => {
  const {
    consents
  } = patient;
  const hasHipaa = Object.hasOwn(consents, 'hipaa') || Object.hasOwn(consents, 'crm');
  const hasRwe = Object.hasOwn(consents, 'realWorldEvidence');
  return hasHipaa && hasRwe;
};

/**
 * Verifies if the user has pending consent for agreement and returns the specific consent page route.
 *
 * @param patient - The patient object containing consent records.
 * @returns The specific route name string if a consent is pending, otherwise null.
 */
export const getPendingConsentRoute = patient => {
  var _consents$hipaa, _consents$crm, _consents$realWorldEv;
  const {
    consents
  } = patient;
  const hasPendingHipaa = Object.hasOwn(consents, 'hipaa') && !((_consents$hipaa = consents.hipaa) !== null && _consents$hipaa !== void 0 && _consents$hipaa.at) || Object.hasOwn(consents, 'crm') && !((_consents$crm = consents.crm) !== null && _consents$crm !== void 0 && _consents$crm.at);
  if (hasPendingHipaa) {
    return 'hipaa';
  }
  const hasPendingRwe = Object.hasOwn(consents, 'realWorldEvidence') && !((_consents$realWorldEv = consents.realWorldEvidence) !== null && _consents$realWorldEv !== void 0 && _consents$realWorldEv.at);
  if (hasPendingRwe) {
    return 'rwe';
  }
  return null;
};